// React
import { useEffect, useState } from 'react';
// Schema
import { PrePostagemSchema, type TPrePostagemSchema } from './schemas';
// Form
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
// Navigate
import { useNavigate } from 'react-router-dom';
// Services
import {
  addInternalComment,
  createColeta,
  createPostage,
  createTicket,
  generateColeta,
  getCep,
  getCurrentUserName,
  parseColeta,
  popupError,
  popupSuccess,
  resolveProblemAndBrand,
  simulatePostageEndDate,
  simulatePostagePrice,
  withTokenRetry,
  zafClient,
} from '@services/';
// Contexts
import { useCorreios } from '@contexts/';
// Hooks
import {
  formatOnlyNumber,
  calcularPesoCubico,
  getFormattedNow,
  formatToMoney,
  formatToDateBR,
  formatToCpfCnpj,
  removeEmptyFields,
  splitNotaFiscal,
  formatToPhone,
  formatToCellphone,
  formatToCEP,
} from '@hooks/';
// Interfaces
import type { ISimulateValues } from './interfaces';
import type {
  CustomObjectRecord,
  ICreateShipment,
  IUserData,
  IZendeskTicket,
  IZendeskUser,
} from '@interfaces/';
import type { Dict } from 'styled-components/dist/types';
// import type { CustomObjectRecordRecipient } from 'interfaces/recipient';

export function useNewShipment() {
  const [step, setStep] = useState<number>(1);
  const [simLoading, setSimLoading] = useState(false);
  const [simulacao, setSimulacao] = useState<ISimulateValues | null>(null);
  const [saveFormData, setSaveFormData] = useState<ICreateShipment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRequester, setUserRequester] = useState<IZendeskUser | null>(null);
  const [saveFormColeta, setSaveFormColeta] = useState<string | null>(null);

  const navigate = useNavigate();
  const { token, area, brandId, handleSetToken, handleSetNewTicketData } = useCorreios();

  const {
    watch,
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<TPrePostagemSchema>({
    resolver: zodResolver(PrePostagemSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      itensDeclaracaoConteudo: [{ conteudo: '', quantidade: '1', valor: '' }],
      diametroInformado: '0',
      operacao: {
        tipoEntrada: 'CORREIOS',
      },
    },
  });

  useEffect(() => {
    (async () => {
      const dataTicket = await zafClient.get('ticket.id');
      const ticketId = dataTicket['ticket.id'];

      const ticketProblem = (await resolveProblemAndBrand(ticketId)).problemId;

      const ticketResponse = (await zafClient.request({
        url: `/api/v2/tickets/${ticketId}.json`,
        type: 'GET',
      })) as { ticket: IZendeskTicket };

      const { ticket } = ticketResponse;

      const requesterResponse = (await zafClient.request({
        url: `/api/v2/users/${ticket.requester_id}.json`,
        type: 'GET',
      })) as { user: IZendeskUser };

      let user = requesterResponse.user;
      if (user.user_fields?.cpf_3)
        user.user_fields.cpf = Number(user.user_fields.cpf_3.replace(/\D/g, ''));
      setUserRequester(user);

      // TODO: MELHORAR A TRATIVA DESSES CAMPOS E USAR COMO ENV DO PROJETO REFERENTE AO ID
      const dataNfe = await zafClient.get('ticket.customField:custom_field_39037859328532');
      const nfe = dataNfe['ticket.customField:custom_field_39037859328532'];

      // Nota e serie
      const dataNotaFical = await zafClient.get('ticket.customField:custom_field_38338833496980');
      const notaFiscal = dataNotaFical['ticket.customField:custom_field_38338833496980'];

      const dataEstabelecimento = await zafClient.get(
        'ticket.customField:custom_field_41239684325780'
      );
      const estabelecimento = dataEstabelecimento['ticket.customField:custom_field_41239684325780'];

      setValue(
        'operacao.referencia',
        typeof notaFiscal === 'string'
          ? `Nota fiscal: ${notaFiscal} - Ticket: ${ticketProblem}`
          : ticketProblem
            ? `Ticket: ${ticketProblem}`
            : ''
      );

      setValue(
        'estabelecimento',
        area === 'SAC AT' ? '15' : typeof estabelecimento === 'string' ? estabelecimento : ''
      );

      setValue('chaveNFe', typeof nfe === 'string' ? nfe : '');
      setValue('numeroNotaFiscal', typeof notaFiscal === 'string' ? notaFiscal : '');

      setValue('remetente.nome', user.name ?? '');
      setValue(
        'remetente.cpfCnpj',
        formatToCpfCnpj(user.user_fields?.cpf?.toString().padStart(11, '0')) ??
          user.user_fields?.cnpj_regex?.toString() ??
          ''
      );

      setValue('remetente.email', user.email ?? '');
      setValue('remetente.telefone', user.phone ? formatToPhone(user.phone) : '');
      setValue(
        'remetente.celular',
        user.user_fields.celular ? formatToCellphone(user.user_fields.celular) : ''
      );
      setValue(
        'remetente.endereco.cep',
        user.user_fields?.cep__endereco ? formatToCEP(user.user_fields?.cep__endereco) : ''
      );
      setValue('remetente.endereco.logradouro', user.user_fields?.endereco_2 ?? '');
      setValue('remetente.endereco.bairro', user.user_fields?.bairro ?? '');
      setValue('remetente.endereco.cidade', user.user_fields?.cidade ?? '');

      setValue('remetente.endereco.numero', user.user_fields?.numero?.toString() ?? '');

      setValue('remetente.endereco.uf', user.user_fields?.estado ?? '');
      setValue('remetente.endereco.complemento', user.user_fields?.complemento ?? '');

      const NFRevendaKey = 'ticket.customField:custom_field_37846936367636';
      const ret = await zafClient.get(NFRevendaKey);
      const nf = ret[NFRevendaKey] as string | undefined;
      setValue('numeroNotaFiscal', nf);

      const fieldKey = 'ticket.customField:custom_field_38546069276180';
      const res = await zafClient.get(fieldKey);
      const recordId = res[fieldKey] as string | null;

      if (!recordId) {
        throw new Error('Nenhum produto selecionado.');
      }

      const record = (await zafClient.request({
        url: `/api/v2/custom_objects/produto/records/${recordId}`,
        type: 'GET',
      })) as { custom_object_record: CustomObjectRecord };

      const productData = record.custom_object_record;

      setValue(
        'comprimentoInformado',
        productData.custom_object_fields.profundidade
          ? productData.custom_object_fields.profundidade?.toString()
          : ''
      );

      setValue(
        'larguraInformada',
        productData.custom_object_fields.largura
          ? productData.custom_object_fields.largura?.toString()
          : ''
      );

      setValue(
        'alturaInformada',
        productData.custom_object_fields.altura
          ? productData.custom_object_fields.altura?.toString()
          : ''
      );

      setValue(
        'pesoInformado',
        productData.custom_object_fields.peso === null ||
          productData.custom_object_fields.peso === undefined
          ? ''
          : String(Math.round(productData.custom_object_fields.peso * 1000))
      );

      setValue(
        'itensDeclaracaoConteudo.0.conteudo',
        productData.custom_object_fields?.codigo_do_produto?.toString() ?? ''
      );
      setValue(
        'itensDeclaracaoConteudo.0.valor',
        formatToMoney(productData.custom_object_fields.valor_base?.toString()) ?? ''
      );

      if (area === 'SAC E-COM') setValue('logisticaReversa', true);
    })();
  }, []);

  const handlePick = (u: IUserData) => {
    setValue('destinatario.nome', u.name ?? '');
    setValue(
      'destinatario.cpfCnpj',
      u.cnpj
        ? formatToCpfCnpj(u.cnpj)
        : u.cpf
          ? formatToCpfCnpj(u.cpf?.toString().padStart(11, '0'))
          : ''
    );
    setValue('destinatario.email', u.email ?? '');
    setValue('destinatario.telefone', u.telefone ? formatToPhone(u.telefone) : '');
    setValue('destinatario.celular', u.celular ? formatToCellphone(u.celular) : '');
    setValue('destinatario.endereco.cep', u.cep ? formatToCEP(u.cep) : '');
    setValue('destinatario.endereco.logradouro', u.endereco_2 ?? '');
    setValue('destinatario.endereco.bairro', u.bairro ?? '');
    setValue('destinatario.endereco.cidade', u.cidade ?? '');
    setValue('destinatario.endereco.uf', u.estado ?? '');
    setValue('destinatario.endereco.numero', u.numero ?? '');
    setValue('destinatario.endereco.complemento', u.complemento ?? '');
  };

  type Step = { name: string; run: () => Promise<void> };

  const formatAxiosError = (err: any) => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const msg =
      (Array.isArray(data?.msgs) && data.msgs.join(' | ')) ||
      data?.msg ||
      data?.message ||
      err?.message ||
      'Erro inesperado';
    return { status, msg };
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const createIncidentWithRetry = async (
    params: Parameters<typeof createTicket>[0],
    attempts = 3
  ) => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        return await createTicket(params);
      } catch (err) {
        lastError = err;
        if (attempt < attempts) {
          await wait(attempt * 800);
        }
      }
    }

    throw lastError;
  };

  useEffect(() => {
    if (watch('operacao.formaEntrada') === 'CA') {
      setValue('logisticaReversa', false);
    }
  }, [watch('operacao.formaEntrada')]);

  const handleStepChange = async () => {
    if (step !== 2 || !saveFormData || !token) {
      setStep((p) => p + 1);
      return;
    }

    setIsSubmitting(true);

    try {
      // Verificar se é SAC AT com postagem reversa para criar 2 postagens
      const isSacAtWithReversa = area === 'SAC AT' && watch('logisticaReversa') === true;
      const numPostagens = isSacAtWithReversa ? 2 : 1;

      let response, errorMsg, xmlData;
      xmlData = null;

      // Array para armazenar os códigos das postagens criadas
      const codigosObjetos: string[] = [];
      const responses: Array<{
        status?: number;
        codigoObjeto?: string;
        prazoPostagem?: string;
        descStatusAtual?: string;
        data?: any;
      }> = [];

      // Criar postagens (1 ou 2 dependendo do caso)
      for (let i = 0; i < numPostagens; i++) {
        // Na segunda postagem (SAC AT + reversa), inverter remetente e destinatário
        const formDataForRequest =
          isSacAtWithReversa && i === 1
            ? {
                ...saveFormData,
                remetente: {
                  ...saveFormData.destinatario,
                  endereco: { ...saveFormData.destinatario.endereco },
                },
                destinatario: {
                  ...saveFormData.remetente,
                  endereco: { ...saveFormData.remetente.endereco },
                },
              }
            : saveFormData;

        if (watch('operacao.formaEntrada') === 'CA' && saveFormColeta != null) {
          response = await createColeta(saveFormColeta);
          xmlData = parseColeta(response.data);
          if (xmlData['cod_erro'] != null && parseInt(xmlData['cod_erro']) !== 0) {
            response.status = 400;
            errorMsg = xmlData['msg_erro'];
          } else if (xmlData['codigo_erro'] != null && parseInt(xmlData['codigo_erro']) !== 0) {
            response.status = 400;
            errorMsg = xmlData['descricao_erro'];
          }
        } else {
          response = await withTokenRetry(
            (t) => createPostage(t, formDataForRequest),
            token.token,
            area,
            handleSetToken
          );
        }

        const status = response?.status ?? 200;

        if (status !== 200) {
          await popupError(`Erro ao criar postagem ${i + 1} (${status}) ${errorMsg}.`, false);
          return;
        }

        const codigoObjeto: string = !xmlData ? response?.codigoObjeto : xmlData['numero_etiqueta'];
        codigosObjetos.push(codigoObjeto);
        responses.push(response);
      }

      // Usar o primeiro código para as operações subsequentes
      const codigoObjeto = codigosObjetos[0];
      const user = await getCurrentUserName();
      const pesoCubico = calcularPesoCubico(
        Number(watch('comprimentoInformado')),
        Number(watch('larguraInformada')),
        Number(watch('alturaInformada'))
      );

      const dataTicket = await zafClient.get('ticket.id');
      const ticketId = dataTicket['ticket.id'];

      // TODO: Codigo duplicado
      const fieldKey = 'ticket.customField:custom_field_38546069276180';
      const res = await zafClient.get(fieldKey);
      const recordId = res[fieldKey] as string | null;

      if (!recordId) {
        await popupError(`Nenhum Produto Selecionado`, false);
        return;
      }

      const record = (await zafClient.request({
        url: `/api/v2/custom_objects/produto/records/${recordId}`,
        type: 'GET',
      })) as { custom_object_record: CustomObjectRecord };

      const productData = record.custom_object_record;

      const codProdutoData = await zafClient.get('ticket.customField:custom_field_38546069276180');
      const codProd = String(codProdutoData['ticket.customField:custom_field_38546069276180']);
      const ticketResponse = (await zafClient.request({
        url: `/api/v2/custom_objects/produto/records/${codProd}`,
        type: 'GET',
      })) as Dict;

      if (codigoObjeto && productData && userRequester && user) {
        // Array para armazenar os tickets criados
        const newTickets = [];
        const failedIncidents: string[] = [];

        // Criar tickets para cada postagem
        for (let i = 0; i < codigosObjetos.length; i++) {
          const currentCodigoObjeto = codigosObjetos[i];
          const currentResponse = responses[i];

          try {
            const newTicket = await createIncidentWithRetry({
              body: {
                codigoObjeto: currentCodigoObjeto,
                tipoPostagem: watch('operacao.formaEntrada'),
                servico: watch('codigoServico'),
                // tipoRegistro,
                valorProduto: watch('itensDeclaracaoConteudo.0.valor'),
                postagemReversa: watch('logisticaReversa'),
                codigoDevolucao: currentCodigoObjeto,
                motivoDevolucao: watch('motivoDevolucao'),
                prazoCorreios: watch('dataValidade'),
                rastreioDevolucao: currentCodigoObjeto,
                statusPostagem: !xmlData
                  ? currentResponse.descStatusAtual
                  : xmlData['status_objeto'],
                observacaoColeta: watch('observacao'),
                notaFical: splitNotaFiscal(watch('numeroNotaFiscal') ?? '').chave,
                estabelecimento: watch('estabelecimento'),
                serie: splitNotaFiscal(watch('numeroNotaFiscal') ?? '').serie,
                produto: watch('itensDeclaracaoConteudo.0.conteudo'),
                tipoEnvioPostagem: watch('tipoPostagem'),
                campoFormaEntrada: watch('campoFormaEntrada'),
                // Coleta campos
                dataSolicitacao: xmlData ? xmlData['data_solicitacao'] : null,
                codigoSolicitacao: xmlData ? xmlData['numero_coleta'] : null,
                //
                codigoProduto:
                  ticketResponse['custom_object_record']['custom_object_fields'][
                    'codigo_do_produto'
                  ],
                quantidade: watch('itensDeclaracaoConteudo.0.quantidade'),
                statusIntegração: 'pendente_lgr',
                tipoDevolucao: 'devolucao',
                statusDevolucao: 'aguardando_devolução_status',
              },
              description: `
            Ticket de Incidente LGR Correios criado ${isSacAtWithReversa ? `(${i + 1}/${numPostagens})` : ''}.
            Produto: ${productData.name} - ${watch('itensDeclaracaoConteudo.0.conteudo')}
            Quantidade: ${watch('itensDeclaracaoConteudo.0.quantidade')}
            Nota fiscal e série: ${splitNotaFiscal(watch('numeroNotaFiscal') ?? '').chave}- ${splitNotaFiscal(watch('numeroNotaFiscal') ?? '').serie}
            ${isSacAtWithReversa ? `Postagem ${i + 1} de ${numPostagens} (SAC AT com Postagem Reversa)` : ''}
            `,
              mainTicketId: ticketId,
              fallbackBrandId: brandId ?? undefined,
              requesterId: userRequester?.id ?? 0,
              productName: productData.name,
              agentId: user.id,
            });

            if (newTicket) {
              newTickets.push(newTicket);
            }
          } catch (err: any) {
            const { status: st, msg } = formatAxiosError(err);
            failedIncidents.push(
              `${currentCodigoObjeto}${st ? ` (${st})` : ''}${msg ? `: ${msg}` : ''}`
            );
            console.error('[createIncident] Falha após retentativas', err);
          }
        }

        if (failedIncidents.length > 0 || newTickets.length !== codigosObjetos.length) {
          await popupError(
            `Postagem(ns) criada(s), mas houve falha ao criar incident para: \n• ${failedIncidents.join('\n• ')}`,
            false
          );
          return;
        }

        if (newTickets.length > 0) {
          const ticketIds = newTickets.map((t) => t.ticket.id).join(', ');
          popupSuccess(
            `Ticket${newTickets.length > 1 ? 's' : ''} criado${newTickets.length > 1 ? 's' : ''} com sucesso: ${ticketIds}`,
            true
          );
          handleSetNewTicketData(newTickets[0]);

          // Criar o comentário com informações de todas as postagens
          const ticketsInfo = newTickets
            .map(
              (ticket, idx) =>
                `Postagem ${idx + 1}: Código de rastreio: ${codigosObjetos[idx]}
              Ticket: [${ticket.ticket.id}](https://atendimento-britania.zendesk.com/agent/tickets/${ticket.ticket.id})`
            )
            .join('\n              ');

          const steps: Step[] = [
            {
              name: 'Adicionar comentário interno',
              run: () =>
                addInternalComment(
                  `Autorização de postagem gerada ${isSacAtWithReversa ? `(${numPostagens} postagens - SAC AT com Postagem Reversa)` : ''}
              ${ticketsInfo}
              Gerado em: ${getFormattedNow()}
              `
                ),
            },
            {
              name: 'Salvar código do objeto (custom_field_38372417820436)',
              run: () =>
                zafClient.set('ticket.customField:custom_field_38372417820436', codigoObjeto),
            },
            {
              name: 'Salvar valor da simulação (custom_field_38375887743124)',
              run: () =>
                zafClient.set('ticket.customField:custom_field_38375887743124', simulacao?.valor),
            },
            {
              name: 'Salvar peso cúbico (custom_field_38375289349012)',
              run: () =>
                zafClient.set('ticket.customField:custom_field_38375289349012', pesoCubico),
            },
            {
              name: 'Salvar peso informado (custom_field_38375337945236)',
              run: () =>
                zafClient.set(
                  'ticket.customField:custom_field_38375337945236',
                  watch('pesoInformado')
                ),
            },
            {
              name: 'Salvar data simulação (custom_field_39002317024916)',
              run: () =>
                zafClient.set('ticket.customField:custom_field_39002317024916', simulacao?.data),
            },
            {
              name: 'Salvar estabelecimento (custom_field_38374638222996)',
              run: () =>
                zafClient.set(
                  'ticket.customField:custom_field_38374638222996',
                  watch('estabelecimento')
                ),
            },
            {
              name: 'Salvar tipo de postagem (custom_field_38379663225748)',
              run: () =>
                zafClient.set(
                  'ticket.customField:custom_field_38379663225748',
                  watch('tipoPostagem')
                ),
            },
            {
              name: 'Salvar motivo de devolução (custom_field_38375149016980)',
              run: () =>
                zafClient.set(
                  'ticket.customField:custom_field_38375149016980',
                  watch('motivoDevolucao')
                ),
            },
            {
              // [LGR] Forma de Entrada:{{ticket.ticket_field_option_title_38374625151636}}
              name: 'Salvar forma de entrada (custom_field_38374625151636)',
              run: () =>
                zafClient.set(
                  'ticket.customField:custom_field_38374625151636',
                  watch('campoFormaEntrada')
                ),
            },
          ];

          const failures: string[] = [];

          for (const step of steps) {
            try {
              await step.run();
            } catch (err: any) {
              const { status: st, msg } = formatAxiosError(err);
              console.error(`[FAIL] ${step.name}`, err);
              failures.push(`${step.name}${st ? ` (${st})` : ''}: ${msg}`);
            }
          }

          if (failures.length > 0) {
            await popupError(
              `Algumas ações foram concluídas, mas ocorreram erros:\n• ${failures.join('\n• ')}`,
              false
            );
            return;
          }

          navigate('/success');
        }
      }
    } catch (err: any) {
      const { status, msg } = formatAxiosError(err);
      console.error('handleStepChange error:', err);
      await popupError(
        `Erro no processo de criação de postagem/incident${status ? ` (${status})` : ''}: ${msg}`,
        false
      );
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 2) setStep((p) => p - 1);
    else navigate('/');
  };

  const values = watch([
    'codigoServico',
    'remetente.endereco.cep',
    'destinatario.endereco.cep',
    'pesoInformado',
    'comprimentoInformado',
    'larguraInformada',
    'alturaInformada',
    'itensDeclaracaoConteudo.0.valor',
  ]);

  const itemValor = watch('itensDeclaracaoConteudo.0.valor') || '';

  const simulate =
    values.every((v) => v !== undefined && v !== null && v !== '') && String(itemValor).length > 4;

  useEffect(() => {
    if (!simulate || !token) return;

    let alive = true;

    (async () => {
      setSimLoading(true);
      try {
        const bodyEndDate = {
          idLote: watch('itensDeclaracaoConteudo')?.[0]?.conteudo,
          parametrosPrazo: [
            {
              cepOrigem: formatOnlyNumber(watch('remetente.endereco.cep')),
              cepDestino: formatOnlyNumber(watch('destinatario.endereco.cep')),
              coProduto: watch('codigoServico'),
              nuRequisicao: '1',
            },
          ],
        };

        const bodyPrice = {
          idLote: watch('itensDeclaracaoConteudo')?.[0]?.conteudo,
          parametrosProduto: [
            {
              coProduto: watch('codigoServico'),
              cepOrigem: formatOnlyNumber(watch('remetente.endereco.cep')),
              cepDestino: formatOnlyNumber(watch('destinatario.endereco.cep')),
              psObjeto: Number(watch('pesoInformado')),
              tpObjeto: 2,
              comprimento: Number(watch('comprimentoInformado')),
              largura: Number(watch('larguraInformada')),
              altura: Number(watch('alturaInformada')),
              diametro: 0,
              nuRequisicao: '1',
            },
          ],
        };

        const [endDateSet, priceSet] = await Promise.allSettled([
          withTokenRetry(
            (t) =>
              simulatePostageEndDate(
                bodyEndDate,
                t,
                area === 'SAC E-COM' ? '0079515169' : '0079515150'
              ),
            token.token,
            area,
            handleSetToken
          ),
          withTokenRetry(
            (t) =>
              simulatePostagePrice(
                bodyPrice,
                t,
                area === 'SAC E-COM' ? '0079515169' : '0079515150'
              ),
            token.token,
            area,
            handleSetToken
          ),
        ]);

        if (endDateSet.status !== 'fulfilled' || priceSet.status !== 'fulfilled') {
          if (endDateSet.status !== 'fulfilled') {
            throw endDateSet.status === 'rejected'
              ? endDateSet.reason
              : new Error('Unknown error in endDateSet');
          } else if (priceSet.status !== 'fulfilled') {
            throw priceSet.status === 'rejected'
              ? priceSet.reason
              : new Error('Unknown error in priceSet');
          }
        }

        const endDateResponse = endDateSet.value;
        const priceResponse = priceSet.value;

        if (endDateResponse.status !== 200 || priceResponse.status !== 200) {
          throw new Error('Erro ao simular postagem, verifique os dados que foram inseridos');
        }

        if (!alive) return;

        setSimulacao({
          valor: priceResponse.data?.[0]?.vlBaseCalculoImposto,
          prazoDiasUteis: endDateResponse.data?.[0]?.prazoEntrega,
          data: endDateResponse.data?.[0]?.dataMaxima,
        });
      } catch (err: any) {
        if (alive) {
          console.error('simulate error:', err);
          popupError(
            err?.message ?? 'Erro ao simular postagem, verifique os dados que foram inseridos',
            false
          );
        }
      } finally {
        if (alive) setSimLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [simulate, token]);

  useEffect(() => {
    if (
      watch('destinatario.endereco.uf') !== watch('remetente.endereco.uf') &&
      !watch('codigoServico')
    ) {
      setValue('codigoServico', '03298');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('destinatario.endereco.uf'), watch('remetente.endereco.uf')]);

  // TODO: Fazer uma funcao referente a esse { shouldValidate: true }
  useEffect(() => {
    if (watch('destinatario.endereco.cep').length === 9 && token) {
      const cep = formatOnlyNumber(watch('destinatario.endereco.cep'));

      (async () => {
        const response = await withTokenRetry(
          (t) => getCep(cep, t),
          token.token,
          area,
          handleSetToken
        );

        if (response.status === 200) {
          setValue('destinatario.endereco.logradouro', response.data.logradouro, {
            shouldValidate: true,
          });
          setValue('destinatario.endereco.bairro', response.data.bairro, { shouldValidate: true });
          setValue('destinatario.endereco.cidade', response.data.localidade, {
            shouldValidate: true,
          });
          setValue('destinatario.endereco.uf', response.data.uf, { shouldValidate: true });
        }
      })();
    }

    if (watch('remetente.endereco.cep').length === 9 && token) {
      const cep = formatOnlyNumber(watch('remetente.endereco.cep'));

      (async () => {
        const response = await withTokenRetry(
          (t) => getCep(cep, t),
          token.token,
          area,
          handleSetToken
        );

        if (response.status === 200) {
          setValue('remetente.endereco.logradouro', response.data.logradouro, {
            shouldValidate: true,
          });
          setValue('remetente.endereco.bairro', response.data.bairro, { shouldValidate: true });
          (setValue('remetente.endereco.cidade', response.data.localidade),
            { shouldValidate: true });
          setValue('remetente.endereco.uf', response.data.uf, { shouldValidate: true });
        }
      })();
    }
  }, [watch('destinatario.endereco.cep'), watch('remetente.endereco.cep'), token]);

  const {
    fields: itensDeclaracaoConteudoFields,
    append: addItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: 'itensDeclaracaoConteudo',
  });

  const handleOnSubmit = async (data: TPrePostagemSchema) => {
    if (token) {
      if (
        data.codigoServico === '03298' &&
        (data.logisticaReversa || watch('operacao.formaEntrada') === 'CA')
      ) {
        data.codigoServico = '03301';
        setValue('codigoServico', '03301');
      }
      if (
        data.codigoServico === '03220' &&
        (data.logisticaReversa || watch('operacao.formaEntrada') === 'CA')
      ) {
        data.codigoServico = '03247';
        setValue('codigoServico', '03247');
      }

      const body = {
        numeroCartaoPostagem: area === 'SAC E-COM' ? '0079515169' : '0079515150', // TODO: TORNAR ISSO UMA VARIAVEL
        codigoServico: data.codigoServico,
        codigoObjeto: data.codigoObjeto,
        codigoFormatoObjetoInformado: '2', // Caixa/Pacote
        alturaInformada: data.alturaInformada!,
        larguraInformada: data.larguraInformada!,
        comprimentoInformado: data.comprimentoInformado!,
        diametroInformado: data.diametroInformado!,
        pesoInformado: data.pesoInformado,

        operacao: {
          tipoEntrada: data.operacao.tipoEntrada,
          formaEntrada: data.operacao.formaEntrada,
          referencia: data.operacao.referencia ?? '',
        },

        precoPrePostagem: data.precoPrePostagem,
        logisticaReversa: data.logisticaReversa === true ? 'S' : 'N',

        remetente: {
          nome: data.remetente.nome,
          cpfCnpj: formatOnlyNumber(data.remetente.cpfCnpj),
          email: data.remetente.email,
          dddTelefone: formatOnlyNumber(data.remetente.telefone).slice(0, 2),
          telefone: formatOnlyNumber(data.remetente.telefone).slice(2),
          dddCelular: formatOnlyNumber(data.remetente.celular).slice(0, 2),
          celular: formatOnlyNumber(data.remetente.celular).slice(2),
          endereco: {
            cep: formatOnlyNumber(data.remetente.endereco.cep),
            logradouro: data.remetente.endereco.logradouro,
            complemento: data.remetente.endereco.complemento,
            numero: data.remetente.endereco.numero,
            bairro: data.remetente.endereco.bairro,
            cidade: data.remetente.endereco.cidade,
            uf: data.remetente.endereco.uf,
          },
        },

        destinatario: {
          nome: data.destinatario.nome,
          cpfCnpj: formatOnlyNumber(data.destinatario.cpfCnpj),
          email: data.destinatario.email,
          dddTelefone: formatOnlyNumber(data.destinatario.telefone).slice(0, 2),
          telefone: formatOnlyNumber(data.destinatario.telefone).slice(2),
          dddCelular: formatOnlyNumber(data.destinatario.celular).slice(0, 2),
          celular: formatOnlyNumber(data.destinatario.celular).slice(2),
          endereco: {
            cep: formatOnlyNumber(data.destinatario.endereco.cep),
            logradouro: data.destinatario.endereco.logradouro,
            complemento: data.destinatario.endereco.complemento,
            numero: data.destinatario.endereco.numero,
            bairro: data.destinatario.endereco.bairro,
            cidade: data.destinatario.endereco.cidade,
            uf: data.destinatario.endereco.uf,
          },
        },

        itensDeclaracaoConteudo: data.itensDeclaracaoConteudo,
        numeroNotaFiscal: data.numeroNotaFiscal ? splitNotaFiscal(data.numeroNotaFiscal).chave : '',
        chaveNFe: data.chaveNFe,

        cienteObjetoNaoProibido: '1',
        prazoPostagem: formatToDateBR(data.dataValidade),
        dataValidade: formatToDateBR(data.dataValidade),
        observacao: data.observacao,

        campoFormaEntrada: data.campoFormaEntrada,
      };

      const cleanedBody = removeEmptyFields(body);
      setSaveFormData(cleanedBody);
      const coletaBody = generateColeta(cleanedBody, body.dataValidade);
      setSaveFormColeta(coletaBody);

      setStep((p) => p + 1);
    }
  };

  // TODO: Mover para arquivo a parte

  const optionsTipoPostagem = [
    { label: 'Garantia', value: 'garantia_tipo_envio_postagem' },
    { label: 'Cortesia', value: 'cortesia_tipo_envio_postagem' },
  ];

  const optionsServico = [
    { label: 'PAC', value: '03298' },
    { label: 'SEDEX', value: '03220' },
  ];

  const optionFormaEntrada = [
    { label: 'A - Autorização de Postagem ', value: 'A' },
    { label: 'CA - Coleta Domiciliar', value: 'CA' },
  ];

  const optionMotivoDevolucao = [
    { label: 'Avaria produto e/ou embalagem', value: 'avaria_produto_e/ou_embalagem' },
    { label: 'Calamidade pública', value: 'calamidade_pública_motivo_devolucao' },
    { label: 'Compra errada - Entrega', value: 'compra_errada_-_entrega' },
    { label: 'Compra errada - Pagamento', value: 'compra_errada_-_pagamento' },
    { label: 'Compra errada - Produto', value: 'compra_errada_-_produto' },
    { label: 'Defeito Estético', value: 'defeito_estético_motivo_devolucao' },
    { label: 'Defeito Funcional', value: 'defeito_funcional_motivo_devolucao' },
    { label: 'Entrega - Atraso', value: 'entrega_-_atraso' },
    { label: 'Entrega - Avaria transportador', value: 'entrega_-_avaria_transportador' },
    { label: 'Entrega - Extravio Parcial', value: 'entrega_-_extravio_parcial' },
    { label: 'Entrega - Extravio Total', value: 'entrega_-_extravio_total' },
    {
      label: 'Entrega - Impossibilidade de entrega',
      value: 'entrega_-_impossibilidade_de_entrega',
    },
    { label: 'Entrega - Parcial', value: 'entrega_-_parcial' },
    { label: 'Envio incorreto - Produto', value: 'envio_incorreto_-_produto' },
    { label: 'Erro de anúncio - Pagamento', value: 'erro_de_anúncio_-_pagamento' },
    { label: 'Erro de anúncio - Produto', value: 'erro_de_anúncio_-_produto' },
    { label: 'Gestão de Crise', value: 'gestão_de_crise' },
    { label: 'Pedido Teste', value: 'pedido_teste_motivo_devolucao' },
    { label: 'Produto Extraviados', value: 'protudo_extraviados' },
    { label: 'Sinistrados', value: 'sinistrados' },
    { label: 'Tombamentos', value: 'tombamentos' },
    {
      label: 'Produto não localizado no ponto de retirada',
      value: 'produto_não_localizado_no_ponto_de_reti',
    },
  ];

  const optionCampoFormaEntrada = [
    { label: 'Parcial', value: 'parcial' },
    { label: 'Total', value: 'total' },
  ];

  return {
    errors,
    watch,
    handleBack,
    step,
    handleOnSubmit,
    isSubmitting,
    register,
    handleSubmit,
    handleStepChange,

    itensDeclaracaoConteudoFields,
    addItem,
    removeItem,

    simulacao,
    simLoading,

    optionsTipoPostagem,
    optionsServico,
    optionFormaEntrada,
    optionMotivoDevolucao,
    optionCampoFormaEntrada,

    handlePick,
    setValue,
  };
}
